import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { Icon } from "./Icon";
import "./SearchMultiSelect.css";

export type SearchMultiSelectOption = {
  id: string;
  label: string;
  sub?: string;
};

type Coords = {
  left: number;
  width: number;
  top: number | null;
  bottom: number | null;
};

interface Props {
  values: string[];
  onChange: (ids: string[]) => void;
  options: SearchMultiSelectOption[];
  placeholder?: string;
  emptyLabel?: string;
  maxHeight?: number;
  disabled?: boolean;
}

const PANEL_MAX = 320;

export function SearchMultiSelect({
  values = [],
  onChange,
  options = [],
  placeholder = "Buscar e selecionar…",
  emptyLabel = "Nenhum resultado.",
  maxHeight = 260,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [coords, setCoords] = useState<Coords | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(
    () => values.map((id) => options.find((o) => o.id === id)).filter(Boolean) as SearchMultiSelectOption[],
    [values, options],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (o) =>
        String(o.label ?? "").toLowerCase().includes(needle) ||
        String(o.sub ?? "").toLowerCase().includes(needle),
    );
  }, [options, q]);

  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const flipUp = spaceBelow < PANEL_MAX && spaceAbove > spaceBelow;
    setCoords({
      left: r.left,
      width: r.width,
      top: flipUp ? null : r.bottom + 6,
      bottom: flipUp ? window.innerHeight - r.top + 6 : null,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    setQ("");
    reposition();
    setTimeout(() => inputRef.current?.focus(), 0);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (id: string) => {
    const set = new Set(values);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange([...set]);
  };

  const remove = (id: string) => onChange(values.filter((v) => v !== id));

  const panelStyle: CSSProperties = coords
    ? {
        left: coords.left,
        width: coords.width,
        top: coords.top != null ? coords.top : "auto",
        bottom: coords.bottom != null ? coords.bottom : "auto",
        maxHeight: PANEL_MAX,
      }
    : { visibility: "hidden" };

  const panel = (
    <div className="sms-panel" style={panelStyle} ref={panelRef}>
      <div className="sms-search">
        <Icon name="search" size={14} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar…"
        />
        {q && (
          <button type="button" className="sms-clear" onClick={() => setQ("")}>
            <Icon name="x" size={12} />
          </button>
        )}
        <span className="sms-count">
          {values.length} selecionad{values.length === 1 ? "a" : "as"}
        </span>
      </div>

      <div className="sms-list" style={{ maxHeight }}>
        {filtered.length === 0 ? (
          <div className="sms-empty">
            <Icon name="search" size={16} />
            <span>{emptyLabel}</span>
          </div>
        ) : (
          filtered.map((o) => {
            const on = values.includes(o.id);
            return (
              <button
                key={o.id}
                type="button"
                className={`sms-opt ${on ? "on" : ""}`}
                onClick={() => toggle(o.id)}
              >
                <span className={`sms-check ${on ? "on" : ""}`}>
                  {on && <Icon name="check" size={11} strokeWidth={3} />}
                </span>
                <span className="sms-opt-l">
                  <span className="sms-opt-label">{o.label}</span>
                  {o.sub && <span className="sms-opt-sub">{o.sub}</span>}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div className="search-multiselect">
      <div
        ref={triggerRef}
        className={`sms-trigger ${open ? "open" : ""} ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        {selected.length === 0 ? (
          <span className="sms-placeholder">{placeholder}</span>
        ) : (
          <span className="sms-chips">
            {selected.map((o) => (
              <span key={o.id} className="sms-chip">
                <span className="sms-chip-label">{o.label}</span>
                <button
                  type="button"
                  className="sms-chip-x"
                  aria-label={`Remover ${o.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(o.id);
                  }}
                >
                  <Icon name="x" size={11} />
                </button>
              </span>
            ))}
          </span>
        )}
        <Icon name="chevron-down" size={14} className="sms-caret" />
      </div>

      {open && createPortal(panel, document.body)}
    </div>
  );
}
